import { useCallback, useEffect, useState } from 'react';
import { withForceRefresh } from '../services/cacheRefreshContext';

const COOLDOWN_SECONDS = 30;
const cooldownKeyFor = (id: string) => `refreshCooldownEndsAt:${id}`;

// Anchored to a sessionStorage timestamp, not component state, so the
// cooldown survives a remount (mirrors ServerUnreachable.tsx's retry
// cooldown) and stays in sync with the backend's 30s per-key Redis lock
// (see withAnalyticsCache.ts).
const getRemainingSeconds = (cooldownKey: string): number => {
  const endsAt = Number(
    sessionStorage.getItem(cooldownKeyFor(cooldownKey)) || 0,
  );
  return Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
};

export function useForceRefresh(
  reload: () => Promise<unknown>,
  cooldownKey: string,
): { refresh: () => void; isRefreshing: boolean; secondsRemaining: number } {
  const [secondsRemaining, setSecondsRemaining] = useState(() =>
    getRemainingSeconds(cooldownKey),
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (secondsRemaining === 0) return undefined;
    const tick = setTimeout(
      () => setSecondsRemaining(getRemainingSeconds(cooldownKey)),
      1000,
    );
    return () => clearTimeout(tick);
  }, [secondsRemaining, cooldownKey]);

  const refresh = useCallback(() => {
    if (getRemainingSeconds(cooldownKey) > 0) return;

    sessionStorage.setItem(
      cooldownKeyFor(cooldownKey),
      String(Date.now() + COOLDOWN_SECONDS * 1000),
    );
    setSecondsRemaining(COOLDOWN_SECONDS);
    setIsRefreshing(true);

    withForceRefresh(reload).finally(() => setIsRefreshing(false));
  }, [reload, cooldownKey]);

  return { refresh, isRefreshing, secondsRemaining };
}
